/**
 * Build-time configuration unique to the standalone Template Studio.
 *
 * Deliberately small. The Studio reuses the dashboard's axios client and its
 * `env.API_URL` base (spec §5.3), so there is no second API-base constant
 * here — only the host origin and the namespace segment Plan 2B injects.
 */

/**
 * Normalize a configured origin to exactly what `event.origin` will carry:
 * lowercase scheme+host, no default port, no path, no trailing slash.
 *
 * postMessage origin checks are exact string equality — `new URL(...).origin`
 * makes "https://Studio.Storemandu.com:443/" and "https://studio.storemandu.com"
 * the same value instead of a silent never-matches bug. Garbage fails loud at
 * boot rather than mysteriously at the first token request.
 */
export function normalizeOrigin(raw: string | undefined): string {
  if (!raw) {
    throw new Error(
      "Missing required build-time config: VITE_DASHBOARD_ORIGIN",
    );
  }
  let origin: string;
  try {
    origin = new URL(raw).origin;
  } catch {
    throw new Error(`VITE_DASHBOARD_ORIGIN is not a valid origin: "${raw}"`);
  }
  // Opaque origins (data:, blob: without an http base, …) serialize to the
  // literal string "null" — an allow-list value that can never legitimately
  // match a host page.
  if (origin === "null") {
    throw new Error(`VITE_DASHBOARD_ORIGIN is not a valid origin: "${raw}"`);
  }
  return origin;
}

/**
 * Exact origin of the dashboard that embeds this app. Used to target outgoing
 * postMessages AND to validate every inbound one — a security boundary, never
 * derived from a URL parameter or an incoming message.
 */
export const DASHBOARD_ORIGIN: string = normalizeOrigin(
  import.meta.env.VITE_DASHBOARD_ORIGIN as string | undefined,
);

/**
 * The namespace segment the Studio's request interceptor injects, replacing
 * the dashboard's `/merchant` | `/system` switch. No `/api/v2` — the axios
 * instance's baseURL already ends with it.
 */
export const TEMPLATE_EDITOR_NAMESPACE = "/merchant/template-editor";
